import { useEffect } from 'react';
import { create } from 'zustand';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { usePostQuery } from '@/queries/usePostQuery';
import { Defect, DefectFormType, DefectSoortcodeEnum, MaakDefectNietInzetBaarFormType } from '@/types/Defecten';
import { bmsApiResponseHandler, responseToToastHandler } from '@/utils/bmsApiResponseHandlers';
import { constructZuluTimeString, formatIsoDate, formatTime } from '@/utils/dateUtils';
import { getDateVariations } from '@/utils/dateVariations';

type DefectFormHelperType = {
  startDatum: string;
  startTijd: string;
  toelichting?: string;
};
type DefectStateType = {
  dateVariations: { label: string; value: string }[];
  form: DefectFormHelperType;
  defect: DefectFormType;
};
type DefectActionType = {
  getDefectPost: () => string;
  setDetails: (details: WerklijnContextMenuDetails) => void;
  updateDate: (date: string) => void;
  updateTime: (date: string) => void;
  updateToelichting: (toelichting: string) => void;
  updateSoort: (soortcode: DefectSoortcodeEnum) => void;
  reset: () => void;
  getIsoDateTime: () => string;
};

type DefectStoreType = DefectStateType & DefectActionType;
const defaultState = () => ({
  dateVariations: getDateVariations(),
  form: {
    startDatum: '',
    startTijd: '',
  },
  defect: {
    eenheidnummer: undefined,
    begindatumtijd: undefined,
    soortcode: DefectSoortcodeEnum.DEFECT_NIET_INZETBAAR,
    toelichting: undefined,
  },
});

const useDefectState = create<DefectStoreType>((set, get) => ({
  ...defaultState(),
  getDefectPost: () => get().form.startDatum,
  getIsoDateTime: () => get().form.startDatum,
  setDetails: (details: WerklijnContextMenuDetails) =>
    set({
      form: {
        startDatum: formatIsoDate(details?.aankomstTijd ?? details?.vertrekTijd ?? new Date()),
        startTijd: formatTime(details?.aankomstTijd ?? details?.vertrekTijd ?? new Date()),
      },
      defect: {
        eenheidnummer: details?.eenheidnummer,
        begindatumtijd: (details?.aankomstTijd ?? details?.vertrekTijd ?? new Date()).toISOString(),
        soortcode: DefectSoortcodeEnum.DEFECT_NIET_INZETBAAR,
      },
    }),
  updateDate: (date: string) =>
    set((state) => ({
      defect: {
        ...state.defect,
        begindatumtijd: constructZuluTimeString(date, state.form.startTijd),
      },
      form: { ...state.form, startDatum: date },
    })),
  updateTime: (time: string) => {
    set((state) => ({
      defect: {
        ...state.defect,
        begindatumtijd: constructZuluTimeString(state.form.startDatum, time),
      },
      form: { ...state.form, startTijd: time },
    }));
  },
  updateToelichting: (toelichting: string) =>
    set((state) => ({
      defect: {
        ...state.defect,
        toelichting,
      },
      form: { ...state.form, toelichting },
    })),
  updateSoort: (soortcode: DefectSoortcodeEnum) =>
    set((state) => ({
      defect: {
        ...state.defect,
        soortcode,
      },
      form: { ...state.form, soortcode },
    })),
  reset: () => set(defaultState()),
}));

const useDefectenDialog = (details: WerklijnContextMenuDetails, handleOpenChange: (open: boolean) => void) => {
  const defectState = useDefectState();
  const { post } = usePostQuery<DefectFormType, Defect>('/defecten/maaknieuwdefect');

  useEffect(() => {
    defectState.setDetails(details);
  }, [details?.eenheidnummer, details?.aankomstTijd, details?.vertrekTijd]);
  // reset all values
  const resetDialog = (closeButtonCLick: boolean) => {
    if (closeButtonCLick) {
      handleOpenChange(false);
    }
    defectState.reset();
  };

  const maakNietInzetbaarFormEvent = (_: EventTarget & MaakDefectNietInzetBaarFormType) => {
    void (async () => {
      try {
        const defect = await post(defectState.defect);
        const message = bmsApiResponseHandler(defect?.id ? 'SUCCES' : 'ERROR', 'niet-inzetbaar-vanaf');
        responseToToastHandler(message);
        if (message.type === 'success') resetDialog(true);
      } catch (error: unknown) {
        // form validation error (most likely).
        responseToToastHandler({
          type: 'error',
          message: 'Onverwachte fout: Probeer het defect opnieuw niet inzetbaar te maken.',
        });
        console.error({ error });
      }
    })();
  };

  return {
    defectState,
    resetDialog,
    maakNietInzetbaarFormEvent,
  };
};
export default useDefectenDialog;
